/**
 * Interactive form primitives on Base UI: Select, Switch, Checkbox, Tabs,
 * SegmentedControl.
 */
import { Checkbox as BaseCheckbox } from '@base-ui-components/react/checkbox'
import { Select as BaseSelect } from '@base-ui-components/react/select'
import { Switch as BaseSwitch } from '@base-ui-components/react/switch'
import { Tabs as BaseTabs } from '@base-ui-components/react/tabs'
import { Check, ChevronDown, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

// --- Select -----------------------------------------------------------------

export interface SelectOption<T extends string> {
  value: T
  label: string
  description?: string
  disabled?: boolean
}

export function Select<T extends string>({
  value,
  onValueChange,
  options,
  placeholder,
  className,
  id,
  size = 'md',
  disabled,
  'aria-label': ariaLabel,
}: {
  value: T | null
  onValueChange: (value: T) => void
  options: SelectOption<T>[]
  placeholder?: string
  className?: string
  id?: string
  size?: 'sm' | 'md'
  disabled?: boolean
  'aria-label'?: string
}) {
  return (
    <BaseSelect.Root
      value={value}
      onValueChange={(next) => onValueChange(next as T)}
      disabled={disabled}
    >
      <BaseSelect.Trigger
        id={id}
        aria-label={ariaLabel}
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 rounded-[var(--radius-md)]',
          'border border-[var(--color-border-strong)] bg-[var(--color-surface)] text-left',
          'text-[var(--color-fg)] outline-none transition-[border-color,box-shadow] duration-150',
          'focus-visible:border-[var(--color-primary)] focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/25',
          'disabled:opacity-50 data-[popup-open]:border-[var(--color-primary)]',
          size === 'sm' ? 'h-7.5 px-2 text-xs' : 'h-9 px-2.5 text-sm',
          className
        )}
      >
        {/* rc.0 has no `placeholder` prop — the empty case is the render fn. */}
        <BaseSelect.Value className="truncate">
          {(current: string | null) => {
            const match = options.find((option) => option.value === current)
            if (match) return match.label
            return <span className="text-[var(--color-fg-subtle)]">{placeholder ?? ''}</span>
          }}
        </BaseSelect.Value>
        <BaseSelect.Icon>
          <ChevronDown className="size-3.5 shrink-0 text-[var(--color-fg-subtle)]" />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>
      <BaseSelect.Portal>
        <BaseSelect.Positioner sideOffset={4} className="z-50" alignItemWithTrigger={false}>
          <BaseSelect.Popup
            className={
              'max-h-72 min-w-[var(--anchor-width)] overflow-y-auto rounded-[var(--radius-lg)] ' +
              'border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-1 shadow-lg outline-none ' +
              'transition-[transform,opacity] duration-150 ' +
              'data-[starting-style]:scale-[0.97] data-[starting-style]:opacity-0 ' +
              'data-[ending-style]:scale-[0.97] data-[ending-style]:opacity-0'
            }
          >
            {options.map((option) => (
              <BaseSelect.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={
                  'flex cursor-default items-start gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm ' +
                  'text-[var(--color-fg)] outline-none data-[highlighted]:bg-[var(--color-surface-hover)] ' +
                  'data-[disabled]:opacity-40'
                }
              >
                <BaseSelect.ItemIndicator className="mt-0.5">
                  <Check className="size-3.5 text-[var(--color-primary)]" />
                </BaseSelect.ItemIndicator>
                <span className={cn('min-w-0 flex-1', !option.description && 'ml-0')}>
                  <BaseSelect.ItemText className="block truncate">{option.label}</BaseSelect.ItemText>
                  {option.description ? (
                    <span className="mt-0.5 block text-2xs text-[var(--color-fg-subtle)]">
                      {option.description}
                    </span>
                  ) : null}
                </span>
              </BaseSelect.Item>
            ))}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  )
}

// --- Switch -----------------------------------------------------------------

export function Switch({
  checked,
  onCheckedChange,
  id,
  disabled,
  'aria-label': ariaLabel,
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  id?: string
  disabled?: boolean
  'aria-label'?: string
}) {
  /**
   * The inset is PADDING, and the travel is the space padding leaves over.
   *
   * 32×18 track, 2px padding all round → a 28×14 well, which a 14px thumb fills
   * vertically and travels 28 − 14 = 14px across. One number to keep right, and
   * it is the same 2px gap on every side in both states.
   *
   * Hand-placing the thumb instead is what was wrong before: a transparent 1px
   * border made the well 30px, the thumb was nudged 2px in from the left and
   * 17px on check — so it ended flush against the right edge with no inset at
   * all, and sat 2px high because nothing centred it (1px above, 3px below).
   */
  return (
    <BaseSwitch.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className={
        'flex h-4.5 w-8 shrink-0 items-center rounded-full bg-[var(--color-neutral-300)] p-0.5 ' +
        'transition-colors duration-150 outline-none ' +
        'focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35 focus-visible:ring-offset-1 ' +
        'data-[checked]:bg-[var(--color-primary)] disabled:opacity-50'
      }
    >
      <BaseSwitch.Thumb
        className={
          'block size-3.5 rounded-full bg-white shadow-xs transition-transform duration-150 ' +
          'data-[checked]:translate-x-[0.875rem]'
        }
      />
    </BaseSwitch.Root>
  )
}

export function SwitchField({
  label,
  hint,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: React.ReactNode
  hint?: React.ReactNode
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 py-1.5">
      <span className="min-w-0">
        <span className="block text-sm text-[var(--color-fg)]">{label}</span>
        {hint ? <span className="mt-0.5 block text-xs text-[var(--color-fg-subtle)]">{hint}</span> : null}
      </span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </label>
  )
}

// --- Checkbox ---------------------------------------------------------------

export function Checkbox({
  checked,
  indeterminate,
  onCheckedChange,
  id,
  disabled,
  'aria-label': ariaLabel,
}: {
  checked: boolean
  indeterminate?: boolean
  onCheckedChange: (checked: boolean) => void
  id?: string
  disabled?: boolean
  'aria-label'?: string
}) {
  return (
    <BaseCheckbox.Root
      id={id}
      checked={checked}
      indeterminate={indeterminate}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className={
        'grid size-4 shrink-0 place-items-center rounded-[var(--radius-xs)] border ' +
        'border-[var(--color-border-strong)] bg-[var(--color-surface)] outline-none transition-colors duration-100 ' +
        'focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35 focus-visible:ring-offset-1 ' +
        'data-[checked]:border-[var(--color-primary)] data-[checked]:bg-[var(--color-primary)] ' +
        'data-[indeterminate]:border-[var(--color-primary)] data-[indeterminate]:bg-[var(--color-primary)] ' +
        'disabled:opacity-40'
      }
    >
      <BaseCheckbox.Indicator className="flex text-[var(--color-primary-fg)] data-[unchecked]:hidden">
        {indeterminate ? <Minus className="size-3" /> : <Check className="size-3" />}
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  )
}

// --- Tabs -------------------------------------------------------------------

export const Tabs = BaseTabs.Root
export const TabPanel = BaseTabs.Panel

export function TabList({ className, ...props }: React.ComponentProps<typeof BaseTabs.List>) {
  return (
    <BaseTabs.List
      className={cn('flex items-center gap-0.5 border-b border-[var(--color-border)]', className)}
      {...props}
    />
  )
}

export function Tab({ className, ...props }: React.ComponentProps<typeof BaseTabs.Tab>) {
  return (
    <BaseTabs.Tab
      className={cn(
        'relative -mb-px border-b-2 border-transparent px-3 py-2 text-sm font-medium outline-none',
        'text-[var(--color-fg-muted)] transition-colors duration-150',
        'hover:text-[var(--color-fg)]',
        'data-[selected]:border-[var(--color-primary)] data-[selected]:text-[var(--color-fg)]',
        'focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35 focus-visible:ring-inset',
        className
      )}
      {...props}
    />
  )
}

// --- Segmented control ------------------------------------------------------

export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  size = 'md',
  className,
}: {
  value: T
  onValueChange: (value: T) => void
  options: { value: T; label: React.ReactNode; title?: string }[]
  size?: 'sm' | 'md'
  className?: string
}) {
  return (
    <div
      role="radiogroup"
      className={cn(
        // The border is what keeps the track visible INSIDE an expanded row,
        // where the block behind it is already the sunken surface and the
        // control would otherwise read as two loose words.
        'inline-flex items-center gap-0.5 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-sunken)] p-0.5',
        className
      )}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={option.title}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'rounded-[var(--radius-sm)] font-medium transition-colors duration-150 outline-none',
              'focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35',
              size === 'sm' ? 'h-6 px-2 text-2xs' : 'h-7 px-2.5 text-xs',
              active
                ? 'bg-[var(--color-surface)] text-[var(--color-fg)] shadow-xs'
                : 'text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]'
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
